/** Vocab types + API client + pure helpers.
 *
 *  Storage is server-side now (DynamoDB via /reader/vocab). Grammar fields
 *  are backfilled server-side by enrich-article-assets when an article's
 *  asset bundle is written to S3; the client only needs to refresh its
 *  vocab list to see them. See features/articles/VocabContext for the
 *  React state layer. */
import { apiClient } from '../../utils/api-client';

export interface InflectionBlock {
  type: string;
  library: string;
  forms: Record<string, string>;
}

export interface VocabEntry {
  vocabId: string;
  userId: string;
  articleId: string;
  chunkId: number;
  text: string;
  translation: string;
  sourceLanguage: string;
  targetLanguage: string;
  context?: string;
  partOfSpeech?: string;
  normalizedForm?: string;
  grammarCase?: string;
  gender?: string;
  voice?: string;
  aspect?: string;
  number?: string;
  tense?: string;
  mood?: string;
  inflections?: InflectionBlock;
  createdAt: string;
}

export type VocabCreatePayload = Omit<VocabEntry, 'vocabId' | 'userId' | 'createdAt'>;

export type ExportType = 'raw' | 'normalized';

const STRIP_EDGE_PUNCT = /^[\p{P}\p{S}]+|[\p{P}\p{S}]+$/gu;

export function stripEdgePunctuation(s: string): string {
  return s.replace(STRIP_EDGE_PUNCT, '').trim();
}

export async function apiListVocab(): Promise<VocabEntry[]> {
  const res = await apiClient.get<{ entries: VocabEntry[] }>('/reader/vocab');
  return res.entries ?? [];
}

export async function apiCreateVocab(payload: VocabCreatePayload): Promise<VocabEntry> {
  const text = stripEdgePunctuation(payload.text);
  const translation = stripEdgePunctuation(payload.translation);
  if (!text || !translation) {
    throw new Error('text and translation must be non-empty after edge-punctuation strip');
  }
  const res = await apiClient.post<{ entry: VocabEntry }>('/reader/vocab', {
    ...payload,
    text,
    translation,
  });
  return res.entry;
}

export async function apiDeleteVocab(articleId: string, vocabId: string): Promise<void> {
  await apiClient.delete<{ success: true }>(
    `/reader/vocab/${encodeURIComponent(articleId)}/${encodeURIComponent(vocabId)}`,
  );
}

export async function apiClearVocab(): Promise<number> {
  const res = await apiClient.delete<{ deleted: number }>('/reader/vocab');
  return res.deleted ?? 0;
}

export function exportVocabAsText(entries: VocabEntry[], exportType: ExportType): string {
  return entries.map((e) => {
    const term = exportType === 'normalized' && e.normalizedForm
      ? e.normalizedForm
      : e.text;
    return `${term}\t${e.translation}`;
  }).join('\n');
}
